// 📝 Auto notes (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.9; mockup
// room-toolkit.html §4): in a Productivity or Research room the chapter pass
// (AnalysisService.generateChapterAnalysisWithNotes, every 5 rounds, one
// flash-lite call) also writes decisions · to-dos · open questions as `notes`
// on the SAME round_analysis row as the chapter.
//
// WHERE THE PAGE GETS THEM (read this before mounting): the chapter row lands
// on the round that just CLOSED, and a fresh page loads only the CURRENT round
// (getRoundInfo + includeCurrentActions) plus the status poll's message types,
// so the live rows alone almost never hold it after a reload. Two sources do:
//   · the Recap archive: getRecapHistory carries the newest notes row (its one
//     notes arm, AnalysisService.latestRoomNotesRows; [] in any other room).
//     Fetch it when the Notes panel is first shown (utils/useLazyOnceFetch's
//     useRecapHistoryFetch, `wanted` = the panel is open, or the room's purpose
//     is learn/research) and use the page's merged `recapActions`.
//   · the socket's new_action: a chapter written while the page is open (no
//     created_at on it: it counts as the newest).
// Hand pickLatestRoomNotes that merged list (live rows first, archive rows
// deduped by id): `pickLatestRoomNotes(recapActions)`.
//
// Dark by construction: only `purpose` 'learn' / 'research' rooms get notes,
// and no room has a purpose until build form v2 ships. The list mirrors the
// backend's AnalysisService.ROOM_NOTES_PURPOSES (tools/smoke-room-notes.js
// pins the parity).

export const ROOM_NOTES_PURPOSES = Object.freeze(['learn', 'research']);
export const ROOM_NOTES_LISTS = Object.freeze(['decisions', 'todos', 'questions']);
export const ROOM_NOTES_MAX_ITEMS = 6;
export const ROOM_NOTES_MAX_CHARS = 140;
// A chapter = 5 closed rounds (ChatGame._maybeGenerateChapterAnalysis's CHAPTER_EVERY).
export const ROOM_NOTES_EVERY_ROUNDS = 5;

export const isNotesRoom = (purpose) => ROOM_NOTES_PURPOSES.includes(purpose);

const oneLine = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
const clip = (s) => (s.length > ROOM_NOTES_MAX_CHARS ? `${s.slice(0, ROOM_NOTES_MAX_CHARS - 1).trimEnd()}…` : s);

// The server already sanitized these (AnalysisService.sanitizeRoomNotes).
// This is the page's own guard: a row is data, never trusted for shape.
// Returns { decisions: string[], todos: [{ text, owner? }], questions: string[] } or null.
export function normalizeRoomNotes(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (!ROOM_NOTES_LISTS.some((k) => Array.isArray(raw[k]))) return null;
  const out = {};
  for (const key of ROOM_NOTES_LISTS) {
    const list = [];
    for (const item of Array.isArray(raw[key]) ? raw[key] : []) {
      if (list.length >= ROOM_NOTES_MAX_ITEMS) break;
      const obj = item && typeof item === 'object' && !Array.isArray(item) ? item : null;
      const text = clip(oneLine(typeof (obj ? obj.text : item) === 'string' ? (obj ? obj.text : item) : ''));
      if (!text) continue;
      if (key !== 'todos') { list.push(text); continue; }
      const owner = obj && typeof obj.owner === 'string' ? oneLine(obj.owner).slice(0, 60) : '';
      list.push(owner ? { text, owner } : { text });
    }
    out[key] = list;
  }
  return out;
}

export const roomNotesCount = (notes) => (notes ? ROOM_NOTES_LISTS.reduce((n, k) => n + notes[k].length, 0) : 0);

const parseData = (d) => {
  if (typeof d !== 'string') return d;
  try { return JSON.parse(d); } catch (e) { return null; }
};

/**
 * The newest notes among the page's action rows: round_analysis rows that
 * carry usable `notes`, newest chapter first (chapter_number, then
 * created_at; a row that just came over the socket has no created_at yet and
 * counts as newest). Returns { notes, updatedAt, chapterNumber, throughRound }
 * or null (no notes yet: the panel's empty state).
 */
export function pickLatestRoomNotes(actions) {
  let best = null;
  for (const a of Array.isArray(actions) ? actions : []) {
    if (!a || a.type !== 'round_analysis') continue;
    const data = parseData(a.data);
    const notes = normalizeRoomNotes(data && data.notes);
    if (!notes) continue;
    const ch = Number(data.chapter_number);
    const chapterNumber = Number.isFinite(ch) && ch > 0 ? ch : 0;
    const t = Date.parse(a.created_at || '');
    const at = Number.isFinite(t) ? t : Infinity;
    if (!best || chapterNumber > best.chapterNumber || (chapterNumber === best.chapterNumber && at > best.at)) {
      best = { notes, at, chapterNumber, updatedAt: Number.isFinite(t) ? a.created_at : null };
    }
  }
  if (!best) return null;
  return {
    notes: best.notes,
    updatedAt: best.updatedAt,
    chapterNumber: best.chapterNumber || null,
    throughRound: best.chapterNumber ? best.chapterNumber * ROOM_NOTES_EVERY_ROUNDS : null,
  };
}

/** "just now" · "4 min ago" · "2 h ago" · "yesterday" · "Sep 27". '' for no time. */
export function notesAgo(updatedAt, now = new Date()) {
  const t = Date.parse(updatedAt || '');
  if (!Number.isFinite(t)) return '';
  const mins = Math.floor((now.getTime() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  if (hours < 48) return 'yesterday';
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** The tail of "Updated after every 5 rounds · last at round 15, 4 min ago" ('' = say nothing). */
export function notesLastLine(updatedAt, throughRound, now = new Date()) {
  const ago = notesAgo(updatedAt, now);
  if (throughRound && ago) return `last at round ${throughRound}, ${ago}`;
  if (throughRound) return `last at round ${throughRound}`;
  return ago ? `last update ${ago}` : '';
}

export function roomNotesFileName(roomName, now = new Date()) {
  const slug = oneLine(roomName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'room';
  return `${slug}-notes-${now.toISOString().slice(0, 10)}.md`;
}

const whenLong = (updatedAt) => {
  const t = Date.parse(updatedAt || '');
  return Number.isFinite(t)
    ? new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
};

/** One readable Markdown file (a person can read it, an AI can take it as notes). */
export function roomNotesToMarkdown(notes, { roomName = '', updatedAt = null, throughRound = null, now = new Date() } = {}) {
  const n = normalizeRoomNotes(notes) || { decisions: [], todos: [], questions: [] };
  const room = oneLine(roomName) || 'This room';
  const when = whenLong(updatedAt);
  const stamp = [when && `Last updated ${when}`, throughRound && `after round ${throughRound}`].filter(Boolean).join(', ');
  const out = [
    `# Notes: ${room}`,
    '',
    `${stamp ? `${stamp}. ` : ''}Written by a small AI note-taker from this chat. It can be wrong.`,
  ];
  const section = (title, items, fmt) => {
    out.push('', `## ${title}`, '');
    if (!items.length) out.push('_Nothing yet._');
    else items.forEach((x) => out.push(fmt(x)));
  };
  section('Decisions', n.decisions, (x) => `- ${x}`);
  section('To-dos', n.todos, (x) => `- [ ] ${x.text}${x.owner ? ` (${x.owner})` : ''}`);
  section('Open questions', n.questions, (x) => `- ${x}`);
  out.push('', `Exported from myspAIce on ${now.toISOString().slice(0, 10)}.`);
  return `${out.join('\n')}\n`;
}
